import React, {useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {DetailSection} from '@/components/football';
import ConfirmModal from '@/components/ui/ConfirmModal';
import Input from '@/components/ui/Input';
import {useToast} from '@/context/ToastContext';
import {useWorkspace} from '@/context/WorkspaceContext';
import {playersV2API} from '@/features/players/api';
import {playerKeys} from '@/features/players/queries';
import {Capability, getV2Error} from '@/services/api/v2';
import {playerDocumentAccept, PlayerDocumentCategoryV2, PlayerDocumentV2} from '@/services/api/playerDocumentsV2';

interface PlayerDocumentTabProps { playerId: string; capabilities: Capability[] }

const viewRevokeDelayMs = 30_000;
const viewRevokeFallbackMs = 120_000;

const PlayerDocumentTab: React.FC<PlayerDocumentTabProps> = ({playerId, capabilities}) => {
  const {currentWorkspace} = useWorkspace();
  const {showToast} = useToast();
  const workspaceId = currentWorkspace?.id ?? '';
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [deleting, setDeleting] = useState<PlayerDocumentV2 | null>(null);
  const [failedDelete, setFailedDelete] = useState<PlayerDocumentV2 | null>(null);
  const [failedView, setFailedView] = useState<PlayerDocumentV2 | null>(null);
  const [category, setCategory] = useState<PlayerDocumentCategoryV2>('external_scout');
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [mutationError, setMutationError] = useState('');
  const [focusError, setFocusError] = useState(false);
  const [fetchingDocument, setFetchingDocument] = useState('');
  const errorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryKey = playerKeys.section(workspaceId, playerId, 'documents', page);
  const documentRootKey = ['v2', 'players', workspaceId, 'detail', playerId, 'documents'] as const;
  const query = useQuery({queryKey, queryFn: () => playersV2API.listDocuments(playerId, {page, pageSize: 20}, workspaceId)});
  const canUploadMedical = capabilities.includes('player.document.upload.medical');
  const canUploadScouting = capabilities.includes('player.document.upload.scouting');
  const uploadCategory = canUploadScouting ? category : 'external_medical';

  useEffect(() => setPage(1), [workspaceId, playerId]);
  useEffect(() => {
    if (!focusError || !mutationError) return;
    // Headless UI restores the trigger when the dialog closes; move focus after that restoration.
    const timer = window.setTimeout(() => {
      errorRef.current?.focus();
      setFocusError(false);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [focusError, mutationError]);

  const upload = useMutation({
    mutationFn: () => playersV2API.uploadDocument(playerId, {file: file!, title: title.trim() || file!.name, category: uploadCategory}, workspaceId),
    onSuccess: async () => {
      setTitle(''); setFile(null); setMutationError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await queryClient.invalidateQueries({queryKey: documentRootKey});
      showToast('Document uploaded', 'success');
    },
    onError: error => setMutationError(getV2Error(error).message),
  });
  const remove = useMutation({
    mutationFn: (documentId: string) => playersV2API.deleteDocument(documentId, workspaceId),
    onSuccess: async () => {
      setDeleting(null); setFailedDelete(null); setMutationError('');
      await queryClient.invalidateQueries({queryKey: documentRootKey});
      showToast('Document deleted', 'success');
    },
    onError: error => {
      setFailedDelete(deleting);
      setDeleting(null);
      setMutationError(getV2Error(error).message);
      setFocusError(true);
    },
  });
  const showViewError = (document: PlayerDocumentV2, message: string) => {
    setFailedDelete(null);
    setFailedView(document);
    setMutationError(message);
    setFocusError(true);
  };
  const scheduleViewRevocation = (popup: Window, url: string) => {
    let delayedTimer: number | undefined;
    let fallbackTimer: number | undefined;
    let revoked = false;
    const revoke = () => {
      if (revoked) return;
      revoked = true;
      if (delayedTimer !== undefined) window.clearTimeout(delayedTimer);
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      URL.revokeObjectURL(url);
    };
    popup.addEventListener('load', () => {
      delayedTimer = window.setTimeout(revoke, viewRevokeDelayMs);
    }, {once: true});
    fallbackTimer = window.setTimeout(revoke, viewRevokeFallbackMs);
  };
  const viewDocument = (document: PlayerDocumentV2) => {
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      showViewError(document, 'Allow pop-ups for this site, then retry viewing the document.');
      return;
    }
    popup.opener = null;
    try {
      popup.document.title = 'Loading document';
      popup.document.body.textContent = `Loading ${document.title}...`;
    } catch {
      // The retained tab can still navigate if the browser restricts blank-page DOM access.
    }
    setFetchingDocument(document.id);
    setMutationError('');
    setFailedView(null);
    void (async () => {
      try {
        const result = await playersV2API.viewDocument(document.id, workspaceId);
        const url = URL.createObjectURL(result.blob);
        scheduleViewRevocation(popup, url);
        popup.location.href = url;
      } catch (error) {
        try { popup.close(); } catch { /* The browser may already have closed it. */ }
        showViewError(document, getV2Error(error).message);
      } finally {
        setFetchingDocument('');
      }
    })();
  };
  const downloadDocument = async (document: PlayerDocumentV2) => {
    setFetchingDocument(document.id);
    setMutationError('');
    try {
      const result = await playersV2API.downloadDocument(document.id, workspaceId);
      const url = URL.createObjectURL(result.blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = result.fileName || document.file_name;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (error) {
      setMutationError(getV2Error(error).message);
    } finally {
      setFetchingDocument('');
    }
  };

  if (query.isPending) return <DetailSection state={{status: 'loading', message: `Loading documents page ${page}...`}} />;
  if (query.isError) return <DetailSection state={{status: 'error', message: 'Unable to load documents.', retryLabel: 'Retry documents', retry: () => { void query.refetch(); }}} />;
  const documents: PlayerDocumentV2[] = query.data?.data ?? [];
  const totalPages = Math.max(1, query.data?.meta.total_pages ?? 1);

  return (
    <div className="min-w-0 space-y-6">
      {mutationError && <div ref={errorRef} role="alert" tabIndex={-1} className="min-w-0 break-words rounded-md bg-red-50 p-3 text-sm text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 dark:bg-red-950 dark:text-red-200">{mutationError}{failedDelete && <button type="button" className="ml-2 min-h-11 rounded-md px-3 font-semibold underline" aria-label={`Retry delete ${failedDelete.title}`} onClick={() => { setMutationError(''); setDeleting(failedDelete); }}>Retry delete</button>}{failedView && <button type="button" className="ml-2 min-h-11 rounded-md px-3 font-semibold underline" aria-label={`Retry view ${failedView.title}`} onClick={() => viewDocument(failedView)}>Retry view</button>}</div>}
      {(canUploadMedical || canUploadScouting) && (
        <form className="min-w-0 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800" onSubmit={event => {
          event.preventDefault();
          if (!file) { setMutationError('Choose a PDF, DOC, or DOCX file.'); return; }
          const validationError = playersV2API.validateDocumentUpload(file);
          if (validationError) { setMutationError(validationError); return; }
          upload.mutate();
        }}>
          <h2 className="break-words text-lg font-semibold text-gray-900 dark:text-gray-100">Upload document</h2>
          <div className="mt-4 grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-3">
            <Input id="document-title" label="Title" value={title} onChange={event => setTitle(event.target.value)} />
            <label htmlFor="document-category" className="min-w-0 break-words text-sm font-medium text-gray-700 dark:text-gray-200">Category<select id="document-category" className="input-field mt-1 min-h-11 w-full" value={uploadCategory} onChange={event => setCategory(event.target.value as PlayerDocumentCategoryV2)}>{canUploadScouting && <option value="external_scout">Scout</option>}{canUploadMedical && <option value="external_medical">Medical</option>}</select></label>
            <label htmlFor="document-file" className="min-w-0 break-words text-sm font-medium text-gray-700 dark:text-gray-200">Document file<input ref={fileInputRef} id="document-file" className="input-field mt-1 min-h-11 w-full max-w-full" type="file" accept={playerDocumentAccept} onChange={event => setFile(event.target.files?.[0] ?? null)} /></label>
          </div>
          <button type="submit" disabled={upload.isPending} className="mt-4 min-h-11 whitespace-normal break-words rounded-md bg-club-blue-700 px-4 text-sm font-semibold text-white disabled:opacity-50">Upload document</button>
        </form>
      )}
      {documents.length === 0 ? <DetailSection state={{status: 'empty', message: page === 1 ? 'No documents uploaded yet.' : 'No documents on this page.'}} /> : (
        <div className="min-w-0 overflow-x-auto rounded-xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
          <table aria-label="Player documents" className="min-w-full text-left text-sm">
            <thead><tr className="text-gray-500 dark:text-gray-400"><th className="p-4">Title</th><th className="p-4">File</th><th className="p-4">Uploaded by</th><th className="p-4">Category</th><th className="p-4"><span className="sr-only">Actions</span></th></tr></thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">{documents.map(document => <tr key={document.id}><th scope="row" className="max-w-xs break-words p-4 text-gray-900 dark:text-gray-100">{document.title}</th><td className="max-w-xs break-all p-4">{document.file_name}</td><td className="max-w-xs break-words p-4">{document.uploader.name}</td><td className="break-words p-4">{document.category === 'external_medical' ? 'Medical' : 'Scout'}</td><td className="p-4"><div className="flex min-w-0 flex-wrap justify-end gap-1"><button type="button" disabled={fetchingDocument === document.id} className="min-h-11 whitespace-normal break-words px-3 font-semibold text-club-blue-700 disabled:opacity-50 dark:text-club-blue-300" aria-label={`View ${document.title}`} onClick={() => viewDocument(document)}>View</button><button type="button" disabled={fetchingDocument === document.id} className="min-h-11 whitespace-normal break-words px-3 font-semibold text-club-blue-700 disabled:opacity-50 dark:text-club-blue-300" aria-label={`Download ${document.title}`} onClick={() => { void downloadDocument(document); }}>Download</button>{document.capabilities.includes('player.document.delete') && <button type="button" className="min-h-11 whitespace-normal break-words px-3 font-semibold text-red-700 dark:text-red-300" aria-label={`Delete ${document.title}`} onClick={() => { setFailedDelete(null); setMutationError(''); setDeleting(document); }}>Delete</button>}</div></td></tr>)}</tbody>
          </table>
        </div>
      )}
      <nav aria-label="documents pagination" className="flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-xl bg-white p-3 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"><button type="button" className="min-h-11 rounded-md px-3 font-semibold text-club-blue-700 disabled:opacity-50 dark:text-club-blue-300" aria-label="Previous documents page" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Previous</button><span className="break-words text-sm text-gray-600 dark:text-gray-300" aria-live="polite">Page {page} of {totalPages}</span><button type="button" className="min-h-11 rounded-md px-3 font-semibold text-club-blue-700 disabled:opacity-50 dark:text-club-blue-300" aria-label="Next documents page" disabled={page >= totalPages} onClick={() => setPage(value => value + 1)}>Next</button></nav>
      <ConfirmModal isOpen={Boolean(deleting)} title="Delete document?" message="This document will be permanently deleted." confirmLabel="Delete document" tone="danger" loading={remove.isPending} onClose={() => setDeleting(null)} onConfirm={() => deleting && remove.mutate(deleting.id)} />
    </div>
  );
};

export default PlayerDocumentTab;
